import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  FormHelperText,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { getInvalidMessagePlaceholders, stepHasVideollamadaValidation } from "./whatsappFlowCatalogs";

const appendTokenToMessageEnd = (currentMessage, token) => {
  let result = currentMessage || "";
  if (result.includes(token)) return result;
  if (!result) return token;
  const endsWithSpace = /\s$/.test(result);
  return endsWithSpace ? `${result}${token}` : `${result} ${token}`;
};

const NOTIFICATION_FIELDS = [
  {
    field: "clientCompletionNotificationMessage",
    label: "Notificación al cliente",
    placeholder: "Ej. Tu flujo fue completado correctamente.",
  },
  {
    field: "commerceCompletionNotificationMessage",
    label: "Notificación al comercio",
    placeholder: "Ej. El cliente completó el flujo.",
  },
  {
    field: "adminNotificationMessage",
    label: "Notificación al administrador",
    placeholder: "Ej. Se completó el flujo del cliente.",
  },
];

export default function WhatsappFlowNotificationsSection({
  data,
  onPatch,
  parameterOptions = [],
}) {
  const [activeField, setActiveField] = useState(NOTIFICATION_FIELDS[0].field);

  const autoCompleteOnEnter = data?.autoCompleteOnEnter === true;
  const autoCancelOnEnter = data?.autoCancelOnEnter === true;
  const lockAutoComplete = stepHasVideollamadaValidation(data?.validationRules);

  const placeholderWarningsByField = useMemo(() => {
    const warnings = {};
    for (const { field } of NOTIFICATION_FIELDS) {
      warnings[field] = getInvalidMessagePlaceholders(data?.[field] || "");
    }
    return warnings;
  }, [data]);

  const handleAutoCompleteToggle = (event) => {
    const checked = event.target.checked;
    onPatch({
      autoCompleteOnEnter: checked,
      autoCancelOnEnter: checked ? false : autoCancelOnEnter,
    });
  };

  const handleAutoCancelToggle = (event) => {
    const checked = event.target.checked;
    onPatch({
      autoCancelOnEnter: checked,
      autoCompleteOnEnter: checked ? false : autoCompleteOnEnter,
    });
  };

  const insertNotificationParameter = (token) => {
    if (!token) return;
    onPatch({
      [activeField]: appendTokenToMessageEnd(data?.[activeField], token),
    });
  };

  const activeFieldLabel =
    NOTIFICATION_FIELDS.find((entry) => entry.field === activeField)?.label ||
    "";

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ color: "#161e55", mb: 1 }}>
        Finalización y notificaciones
      </Typography>
      <Stack spacing={2}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={autoCompleteOnEnter}
                onChange={handleAutoCompleteToggle}
                disabled={lockAutoComplete}
              />
            }
            label={
              <Typography variant="body2">
                Completa el flujo al entrar a este paso
              </Typography>
            }
          />
          {lockAutoComplete ? (
            <FormHelperText>
              Activado automáticamente por la validación Videollamada.
            </FormHelperText>
          ) : null}
          <FormControlLabel
            control={
              <Switch
                checked={autoCancelOnEnter}
                onChange={handleAutoCancelToggle}
              />
            }
            label={
              <Typography variant="body2">
                Cancela el flujo al entrar a este paso
              </Typography>
            }
          />
          <FormHelperText>
            Un paso no puede completar y cancelar el flujo a la vez: activar uno
            desactiva el otro.
          </FormHelperText>
        </Box>

        {NOTIFICATION_FIELDS.map(({ field, label, placeholder }) => (
          <React.Fragment key={field}>
            {placeholderWarningsByField[field].length > 0 ? (
              <Alert severity="warning">
                Placeholders inválidos en «{label}»:{" "}
                {placeholderWarningsByField[field].join(", ")}
              </Alert>
            ) : null}
            <TextField
              label={label}
              value={data?.[field] || ""}
              onChange={(event) => onPatch({ [field]: event.target.value })}
              onFocus={() => setActiveField(field)}
              fullWidth
              multiline
              minRows={2}
              placeholder={placeholder}
              error={placeholderWarningsByField[field].length > 0}
              helperText="Opcional. Puedes usar placeholders {{...}} como en el mensaje principal."
            />
          </React.Fragment>
        ))}

        {parameterOptions.length > 0 ? (
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#161e55", mb: 1 }}
            >
              Parámetros de las notificaciones
            </Typography>
            <Paper
              variant="outlined"
              sx={{ maxHeight: 180, overflow: "auto", borderRadius: "12px" }}
            >
              <List dense disablePadding>
                {parameterOptions.map((parameter) => (
                  <ListItemButton
                    key={`notification-param-${parameter.value}`}
                    onClick={() => insertNotificationParameter(parameter.value)}
                  >
                    <ListItemText
                      primary={parameter.label}
                      secondary={
                        parameter.description
                          ? `${parameter.value} — ${parameter.description}`
                          : parameter.value
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
            <FormHelperText sx={{ mt: 1 }}>
              Toca un parámetro para insertar su token al final de «
              {activeFieldLabel}» (el último campo de notificación con foco).
            </FormHelperText>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}
